import { getODataApiClient } from "../utils/kiota-client";
import type { ProductGroupDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Odata/V1/models/index";

export interface ProductGroupListResult {
  value: ProductGroupDto[];
  "@odata.count"?: number;
  error?: {
    message: string;
    statusCode?: number;
  };
}

export interface ProductGroupFilters {
  page?: number;
  pageSize?: number;
  registrationCode?: string;
  productGroupName?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Loads product groups from the OData API using the Kiota client
 */
export async function loadProductGroups(
  filters: ProductGroupFilters = {},
): Promise<ProductGroupListResult> {
  try {
    const {
      page = 1,
      pageSize = 10,
      registrationCode,
      productGroupName,
      startDate,
      endDate,
    } = filters;

    const filterConditions: string[] = [];

    if (registrationCode) {
      filterConditions.push(`code eq ${registrationCode}`);
    }

    if (productGroupName) {
      const searchLower = productGroupName.toLowerCase().replace(/'/g, "''");
      filterConditions.push(`contains(tolower(name), '${searchLower}')`);
    }

    if (startDate) {
      filterConditions.push(`startDate ge ${startDate}`);
    }

    if (endDate) {
      filterConditions.push(`endDate le ${endDate}`);
    }

    const filterString =
      filterConditions.length > 0 ? filterConditions.join(" and ") : undefined;

    const queryConfig = {
      queryParameters: {
        count: true,
        top: pageSize,
        skip: (page - 1) * pageSize,
        ...(filterString && {
          filter: filterString,
        }),
      },
    };

    const apiClient = getODataApiClient();
    const response = await apiClient.productGroupOverview.get(queryConfig);

    if (!response) {
      return {
        value: [],
        "@odata.count": 0,
      };
    }

    const count = (response as any).additionalData?.["@odata.count"];
    const valueLength = response.value?.length || 0;

    const estimatedCount =
      count !== undefined
        ? count
        : valueLength === pageSize
          ? (page + 1) * pageSize
          : page * pageSize - (pageSize - valueLength);

    return {
      value: response.value || [],
      "@odata.count": estimatedCount,
    };
  } catch (error) {
    console.error("Failed to load product groups:", error);

    const statusCode =
      (error as any)?.statusCode || (error as any)?.response?.status;

    return {
      value: [],
      "@odata.count": 0,
      error: {
        message:
          error instanceof Error ? error.message : "Failed to load product groups",
        statusCode,
      },
    };
  }
}

/**
 * Loads a single product group by its code
 */
export async function loadProductGroupByCode(
  productGroupCode: number,
): Promise<ProductGroupDto | null> {
  try {
    const apiClient = getODataApiClient();
    const response = await apiClient.productGroupOverview.byCode(productGroupCode).get();

    return response || null;
  } catch (error) {
    console.error(`Failed to load product group ${productGroupCode}:`, error);
    return null;
  }
}
